import DOMPurify from 'dompurify'

/**
 * Building blocks of the sandboxed mail frame (security audit 2026-11, platform
 * lead 4). Mail HTML from Graph comes from outside senders, so it never enters
 * the app's own DOM: it is sanitised here and shown only inside an iframe with
 * an opaque origin (no `allow-same-origin`), no forms and no top navigation.
 * Position tricks (a `position: fixed` overlay) stay inside the frame's box.
 */

/**
 * Sandbox flags of the mail frame. `allow-scripts` is there only for the one
 * height script below; without `allow-same-origin` the frame stays an opaque
 * origin, so even that script cannot reach the app, its storage or its cookies.
 * The frame CSP lets nothing but that exact script run. No `allow-forms`,
 * `allow-same-origin` or `allow-top-navigation`; links may open a new tab, and
 * that tab is not sandboxed so the linked site works normally.
 */
export const MAIL_FRAME_SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox'

/**
 * Upper bound for a reported height in px; a longer mail scrolls inside the
 * frame. A second guard behind the script's feedback freeze, low enough that a
 * runaway never leaves a huge blank frame, high enough for a long newsletter.
 */
export const MAIL_FRAME_MAX_HEIGHT = 8000

/** `type` of the frame's height message to the parent. */
export const MAIL_HEIGHT_MESSAGE = 'vonk-mail-height'

/**
 * The frame's only script: it reports the document height to the parent on
 * DOMContentLoaded, on load, on every body resize and on every image load.
 * The id comes from the `data-mail-id` on <html>, so the script text (and with
 * it the CSP hash) is the same for every mail.
 *
 * Feedback guard: `vh` in mail CSS is relative to the frame height, so a body
 * of `height:110vh` grows with every height the parent applies (a runaway
 * loop). Growth with a cause (an image or font load, a width change, window
 * load) is always reported; growth without one is reported at most twice in a
 * row, after that the script freezes until the next cause. A late image
 * therefore still lands on the right height, a vh loop stops after two steps.
 *
 * Keep it free of backslashes, single quotes and `${`: MAIL_FRAME_SCRIPT_HASH is
 * the SHA-256 of exactly this string, and a test recomputes it.
 */
export const MAIL_FRAME_SCRIPT =
  '(function(){var id=document.documentElement.getAttribute("data-mail-id");var last=-1,width=-1,cause=true,strikes=0,frozen=false;function mark(){cause=true;strikes=0;frozen=false}function send(){var body=document.body;if(!body)return;var w=window.innerWidth;if(w!==width){width=w;mark()}var h=Math.ceil(body.getBoundingClientRect().height);if(h===last)return;if(h>last&&last>=0&&!cause){strikes+=1;if(strikes>2)frozen=true}cause=false;if(frozen)return;last=h;parent.postMessage({type:"vonk-mail-height",id:id,height:h},"*")}function onLoad(e){var t=e.target;if(t&&t.tagName==="IMG"){mark();send()}}function start(){send();if(window.ResizeObserver)new window.ResizeObserver(send).observe(document.body)}if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",start);else start();window.addEventListener("load",function(){mark();send()});document.addEventListener("load",onLoad,true);document.addEventListener("error",onLoad,true);if(document.fonts&&document.fonts.addEventListener)document.fonts.addEventListener("loadingdone",function(){mark();send()})})()'

/** base64 SHA-256 of MAIL_FRAME_SCRIPT (checked by SandboxedMailHtml.test.tsx). */
export const MAIL_FRAME_SCRIPT_HASH = 'sha256-O/YxGvY08ZXRtu5iWyqgpelDsNOY9zfZyMky6WMMQMQ='

/**
 * Policy inside the frame, the first element of its <head>. Only the height
 * script may run (by hash, no 'unsafe-inline'), so a script smuggled past
 * DOMPurify still cannot execute; besides that only images and the inline
 * styles mail relies on load. A srcdoc document also inherits the app's CSP:
 * if that ever gets a script-src, it must allow this hash as well.
 */
export const MAIL_FRAME_CSP = [
  "default-src 'none'",
  `script-src '${MAIL_FRAME_SCRIPT_HASH}'`,
  'img-src https: http: data:',
  "style-src 'unsafe-inline'",
  'font-src data:',
  "form-action 'none'",
].join('; ')

/**
 * Removed with their attributes. Form controls have no use in a read-only mail
 * and are the material for a fake login prompt; media could autoplay. `style`
 * elements stay out as before: they could restyle the app if mail HTML ever
 * reached the page.
 */
const FORBID_TAGS = [
  'style',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'optgroup',
  'datalist',
  'fieldset',
  'output',
  'audio',
  'video',
  'source',
  'track',
]
const FORBID_ATTR = ['action', 'formaction', 'method']

let purifier: ReturnType<typeof DOMPurify> | null = null

/**
 * A DOMPurify instance of its own, so the link hook does not leak into the
 * global instance that RichTextView and others use.
 */
function mailPurifier(): ReturnType<typeof DOMPurify> {
  if (purifier) return purifier
  const instance = DOMPurify(window)
  instance.addHook('afterSanitizeAttributes', (node) => {
    if ((node.tagName !== 'A' && node.tagName !== 'AREA') || !node.hasAttribute('href')) return
    // An in-mail anchor (#section) has no target outside the frame: leave it inert
    // instead of opening about:srcdoc in a new tab.
    if ((node.getAttribute('href') ?? '').trim().startsWith('#')) {
      node.removeAttribute('href')
      node.removeAttribute('target')
      return
    }
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  })
  purifier = instance
  return instance
}

/** Sanitise inbound mail HTML: html profile, no form controls, external links open in a new tab. */
export function sanitizeMailHtml(html: string): string {
  return mailPurifier().sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS,
    FORBID_ATTR,
  })
}

/** The app's look, resolved to literal values the frame (another document) can use. */
export interface MailFrameTheme {
  colorScheme: string
  /** The app's root font size in px; the frame's rem follows it. */
  rootFontSize: string
  fontFamily: string
  foreground: string
  primary: string
  border: string
  mutedForeground: string
  muted: string
}

const FALLBACK_THEME: MailFrameTheme = {
  colorScheme: 'normal',
  rootFontSize: '16px',
  fontFamily: "'Figtree Variable', 'Inter', 'Segoe UI', system-ui, sans-serif",
  foreground: 'CanvasText',
  primary: 'LinkText',
  border: 'GrayText',
  mutedForeground: 'GrayText',
  muted: 'transparent',
}

/** Read the current theme from the document root (light or `.dark`), with system fallbacks. */
export function readMailFrameTheme(): MailFrameTheme {
  const style = getComputedStyle(document.documentElement)
  const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback
  return {
    colorScheme: style.colorScheme || FALLBACK_THEME.colorScheme,
    rootFontSize: style.fontSize || FALLBACK_THEME.rootFontSize,
    fontFamily: token('--font-sans', FALLBACK_THEME.fontFamily),
    foreground: token('--foreground', FALLBACK_THEME.foreground),
    primary: token('--primary', FALLBACK_THEME.primary),
    border: token('--border', FALLBACK_THEME.border),
    mutedForeground: token('--muted-foreground', FALLBACK_THEME.mutedForeground),
    muted: token('--muted', FALLBACK_THEME.muted),
  }
}

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

/**
 * The frame's stylesheet. It mirrors `.vonk-prose` and the text-sm body of the
 * old inline rendering (index.css, spec-64 U-091); tables stay unstyled because
 * mail uses them for layout.
 */
export function mailFrameCss(theme: MailFrameTheme): string {
  return [
    `html{font-size:${theme.rootFontSize};color-scheme:${theme.colorScheme};overflow-x:hidden}`,
    'html,body{margin:0;padding:0;background:transparent}',
    '*,::before,::after{box-sizing:border-box}',
    `.mail-body{display:block;overflow-x:auto;overflow-y:hidden;font-family:${theme.fontFamily};font-size:.875rem;line-height:1.55;color:${theme.foreground};overflow-wrap:anywhere}`,
    '.mail-body :where(p,h1,h2,h3,h4,h5,h6,ul,ol,li,blockquote,pre,figure,dl,dd){margin:0}',
    '.mail-body>:first-child{margin-top:0}',
    '.mail-body>:last-child{margin-bottom:0}',
    '.mail-body :where(p){margin:0 0 .6em}',
    '.mail-body :where(h1,h2){font-weight:600;font-size:1.125rem;line-height:1.3;margin:.8em 0 .35em}',
    '.mail-body :where(h3,h4,h5,h6){font-weight:600;font-size:1rem;line-height:1.35;margin:.7em 0 .3em}',
    '.mail-body :where(ul){list-style:disc;padding-left:1.4em;margin:0 0 .6em}',
    '.mail-body :where(ol){list-style:decimal;padding-left:1.4em;margin:0 0 .6em}',
    '.mail-body :where(li+li){margin-top:.25em}',
    '.mail-body :where(li>p){margin:0}',
    `.mail-body :where(a){color:${theme.primary};text-decoration:underline;text-underline-offset:2px}`,
    '.mail-body :where(strong,b){font-weight:600}',
    `.mail-body :where(blockquote){border-left:3px solid ${theme.border};padding-left:.8em;margin:0 0 .6em;color:${theme.mutedForeground}}`,
    `.mail-body :where(code){font-family:${MONO};font-size:.875em;background:${theme.muted};border-radius:4px;padding:.1em .3em}`,
    `.mail-body :where(pre){font-family:${MONO};font-size:.875em;background:${theme.muted};border-radius:6px;padding:.6em .8em;margin:0 0 .6em;overflow-x:auto}`,
    '.mail-body :where(pre code){font-size:inherit;background:none;padding:0}',
    `.mail-body :where(hr){border:none;border-top:1px solid ${theme.border};margin:.8em 0}`,
    '.mail-body :where(img){max-width:100%;height:auto}',
  ].join('\n')
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

/**
 * The frame's full document. The CSP meta is the very first element of <head>,
 * so it governs everything after it; then no referrer, links to a new tab, the
 * stylesheet, the height script, and the sanitised mail. The script sits in the
 * head (before any mail markup) and waits for DOMContentLoaded.
 */
export function buildMailSrcDoc(cleanHtml: string, css: string, id: string): string {
  return [
    `<!doctype html><html data-mail-id="${escapeAttr(id)}"><head>`,
    `<meta http-equiv="Content-Security-Policy" content="${MAIL_FRAME_CSP}">`,
    '<meta charset="utf-8">',
    '<meta name="referrer" content="no-referrer">',
    '<base target="_blank">',
    `<style>${css}</style>`,
    `<script>${MAIL_FRAME_SCRIPT}</script>`,
    `</head><body><div class="mail-body">${cleanHtml}</div></body></html>`,
  ].join('')
}
